import { Notify } from "react-redux-notify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Alert, Button, Image } from "react-bootstrap";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Footer from "./Footer/Footer";
import HeaderIndex from "./Header/HeaderIndex";
import UserConstantsHelper from "../../helpers/constants/user";
import { setThemeMode } from "../../store/actions/ThemeAction";
import { fetchUserDetailsStart } from "../../store/actions/UserAction";
import UnverifiedPerformerModal from "../Modals/UnverifiedPerformerModal";
import PerformerProfilePictureMissing from "../Modals/PerformerProfilePictureMissing";
import CardAddressMismatchModal from "../Modals/CardAddressMismatchModal";
import CardManualReviewRequiredModal from "../Modals/CardManualReviewRequiredModal";
import F2FPackageSelection from "../Face2Face/components/F2FPackageSelection";
import Welcome from "../LandingPageIndex/Welcome";
import { isAuthenticated } from "../../helpers/authentication/authentication";
import { hideUnverifiedPerformerModal, hideCardAddressMismatchModal, hidePerformerMissingProfilePictureModal, hideCardManualReviewRequiredModal } from "../../store/actions/ModalAction";
import { SocketContext as StreamingSocketContext } from "../../helpers/contexts/streaming";
import { SocketContext as CommunicationSocketContext } from "../../helpers/contexts/communication";
import { SocketContext as Face2FaceSocketContext } from "../../helpers/contexts/face2face";

import { getDisplayname } from "../../helpers/storage/user";
import { getBaseUrl } from "../../helpers/constants/resource";
import Error from "../Pages/ErrorPages/Error";

const PathsForAlerts = ["/", "/live"];
const PathsForAdAlerts = ["/ads"];
const PathsForCards = ["/", "/cards"];
const PathsForFace2Face = ["/", "/face2face"];

const MainLayout = (props) => {
  const path = props?.children?.props?.match?.path;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isVisibleSide, setIsVisibleSide] = useState(false);
  const StreamingService = useContext(StreamingSocketContext);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [modalShow, setModalShow] = useState("welcome");
  const [isProcessed, setIsProcessed] = useState(false)
  const Face2FaceService = useContext(Face2FaceSocketContext);
  const CommunicationService = useContext(CommunicationSocketContext);

  const darkMode = useSelector((state) => state.theme.isDark);
  const profile = useSelector((state) => state.users.profile);
  const wallet = useSelector((state) => state.wallet.walletData);
  const unverifiedPerformerModal = useSelector(
    (state) => state.modals.unverifiedPerformerModal
  );
  const performerMissingProfilePicture = useSelector(
    (state) => state.modals.performerMissingProfilePicture
  );
  const cardAddressMismatchModel = useSelector(
    (state) => state.modals.cardAddressMismatchModel
  );
  const errorComponent = useSelector((state) => state.component.errorComponent);
  const userDetails = useSelector((state) => state.otherUser.userDetails);
  const singlePost = useSelector((state) => state.post.singlePost);

  const cardManualReviewRequiredModel = useSelector(
    (state) => state.modals.cardManualReviewRequiredModel
  );

  const appTheme = useTheme();
  const fullscreen = useMediaQuery(appTheme.breakpoints.down("xs"));

  useEffect(() => {
    if (!StreamingService.connected) {
      StreamingService.connect();
    }
  }, [StreamingService.connected]);

  useEffect(() => {
    if (
      !Face2FaceService.connected &&
      UserConstantsHelper.isPerformer(profile.data) ||
      (location.pathname === '/face2face' || location.pathname === '/user-call-f2f')) {
      Face2FaceService.connect();
    }

    localStorage.removeItem('session_data')
  }, [Face2FaceService.connected, profile]);

  useEffect(() => {
    // Only connect if user is authenticated
    if (isAuthenticated() && !CommunicationService.connected && !CommunicationService.isConnecting) {
      CommunicationService.connect();
    }
  }, [CommunicationService.connected, CommunicationService.isConnecting]);

  useEffect(() => {
    if (isAuthenticated() && profile.loading) {
      dispatch(fetchUserDetailsStart());
    }

    window.addEventListener('mention-clicked', (e) => {
      navigate(e.value.link)
    })
  }, []);

  // Handle authentication state changes for socket connection
  useEffect(() => {
    if (CommunicationService?.handleAuthStateChange) {
      CommunicationService.handleAuthStateChange();
    }
  }, [isAuthenticated()]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (localStorage.getItem("isDark") === "true") {
      setDarkMode(true);
    }
    if (isVisibleSide) {
      setIsVisibleSide(false);
    }
    checkMode();
    // Skip on the very first mount: with the SSR profile shell, a user can
    // already be scrolled into real content before hydration finishes, and
    // this effect firing on mount (not just on real navigations) would snap
    // them back to the top the moment the app/header takes over.
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  function designChange() {
    setIsVisibleSide(false);
    if (darkMode === true) {
      setDarkMode(false);
      localStorage.setItem("isDark", false);
    } else {
      setDarkMode(true);
      localStorage.setItem("isDark", true);
    }
  }

  function checkMode() {
    if (localStorage.getItem("isDark") == "true") {
      setDarkMode(true);
    } else {
      setDarkMode(false);
    }
  }

  function isSidebar() {
    if (isVisibleSide) {
      setIsVisibleSide(false);
    } else {
      setIsVisibleSide(true);
    }
  }

  const setDarkMode = (mode) => {
    dispatch(setThemeMode(mode));
  };

  const handleUnverifiedDialogClose = () => {
    dispatch(hideUnverifiedPerformerModal());
  };

  const handlePerformerProfilePictureMissingDialogClose = () => {
    dispatch(hidePerformerMissingProfilePictureModal())
  }

  const handleCardAddressMismatchModalClose = () => {
    dispatch(hideCardAddressMismatchModal())
  }

  const handleCardManualReviewRequiredModalClose = () => {
    dispatch(hideCardManualReviewRequiredModal())
    navigate("/cards");
  }

  const handleOpenCreatePackage = (e) => {
    e.preventDefault()
    if (location.pathname === "/face2face") {
      const params = new URLSearchParams(location.search)
      params.append('createPackage', true)
      navigate({ search: params.toString() })
    } else {
      navigate("/face2face?type=All&createPackage=true")
    }
  }

  const handleViewPage = () => {
    setIsProcessed(true)
    setTimeout( () => {
      setModalShow("login")
      setIsProcessed(false)
    }, 10)
  }

  const isPerformerOnAdCreate = UserConstantsHelper.isPerformer(profile.data) && PathsForAdAlerts.includes(location.pathname);

  const combinedAlerts = [
    UserConstantsHelper.isPerformerDocumentApprovedOrPending(profile.data) && PathsForAlerts.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_error.svg"
          }
          alt='error icon'
        />{" "}
        Hey{" "}
        <span className="uppercaseText">
          {profile.data.username}
        </span>{" "}
        your performer account is not verified. Please click "Verify
        Now" for access to further features. Having an issue with
        verification?{" "}
        <a href="#" className="pink" aria-label="support">
          Support Center
        </a>
        <Button
          variant="primary"
          className="btn-sm pull-right credit"
          type="button"
          onClick={() => navigate("/become-performer")}
        >
          Verify Now
        </Button>
      </Alert>
    ),
    UserConstantsHelper.isDocumentPending(profile.data) && PathsForAlerts.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_warning.svg"
          }
          alt='warning icon'
        />{" "}
        Hey {profile.data.username} your account is under review. It
        will take 2-3 business day to review your account. Have
        Questions? &nbsp;{" "}
        <a href="#" className="pink" aria-label="support">
          Support Center
        </a>
      </Alert>
    ),
    UserConstantsHelper.isUserNeedToUpdateProfile(profile.data)?.showAlert && PathsForAlerts.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            `${getBaseUrl()}/assets/images/icons/user_error.svg`
          }
        />{" "}
        {
          UserConstantsHelper.isUserNeedToUpdateProfile(profile.data)?.message
        }
        <Button
          variant="primary"
          className="btn-sm pull-right credit"
          type="button"
          onClick={() => navigate("/settings")}
        >
          Update profile
        </Button>
      </Alert>
    ),
    UserConstantsHelper.isRegular(profile.data) &&
    !UserConstantsHelper.hasVerifiedCards(profile.data) && PathsForAlerts.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_error.svg"
          }
          alt='error icon'
        />{" "}
        Hey{" "}
        <span className="uppercaseText">
          {profile.data.username}
        </span>
        , your account has no valid card. Please click on "Add
        Card" to add a new card.
        <Button
          variant="primary"
          className="btn-sm pull-right credit"
          type="button"
          onClick={() => navigate("/cards")}
        >
          Add card
        </Button>
      </Alert>
    ),
    (UserConstantsHelper.isRegular(profile.data) || isPerformerOnAdCreate) &&
    wallet?.data?.user_wallet?.remaining === 0 && (PathsForAlerts.includes(location.pathname) || PathsForAdAlerts.includes(location.pathname)) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_error.svg"
          }
          alt='error icon'
        />{" "}
        Hey{" "}
        <span className="uppercaseText">
          {profile.data.username}
        </span>
        , your wallet balance is low. Please click on "Add Fund"
        to add funds to your wallet.
        <Button
          variant="primary"
          className="btn-sm pull-right credit"
          type="button"
          onClick={() => navigate("/wallet")}
        >
          Add Fund
        </Button>
      </Alert>
    ),
    PathsForCards.includes(location.pathname) && profile.data.cards_to_be_verified_count > 0 && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            `${getBaseUrl()}/assets/images/icons/user_error.svg`
          }
        />{" "}
        Hey {getDisplayname()}! In order to utilize your card,
        it is necessary to complete the verification process. Kindly proceed to the "Cards" section and verify your card.
        <Button
          variant="primary"
          className="btn-sm pull-right credit"
          type="button"
          onClick={() => navigate("/cards")}
        >
          Verify card
        </Button>
      </Alert>

    ),
    UserConstantsHelper.isFace2facePackageMissingForPerformer(profile.data) && PathsForFace2Face.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_warning.svg"
          }
          alt='warning icon'
        />{" "}
        Set up your Face2Face packages <a href="#" onClick={handleOpenCreatePackage} className="pink" aria-label="support"> here </a> to receive calls!
      </Alert>
    ),
    UserConstantsHelper.isPerformerUsingDefaultFace2facePackage(profile.data) && PathsForFace2Face.includes(location.pathname) && (
      <Alert variant="light" className="mt-1 not_verify_suggest">
        <Image
          src={
            "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_warning.svg"
          }
          alt='warning icon'
        />{" "}
        You are using default Face2Face packages, you can update them <a href="#" onClick={handleOpenCreatePackage} className="pink" aria-label="support"> here </a>
      </Alert>
    )
  ].filter(Boolean);

  const showAgeRestrictionModal = (window.location.pathname === '/' || window.location.pathname === '/video-store');

  return (
    <>
      {modalShow === "welcome" && !isAuthenticated() && showAgeRestrictionModal ? (
        <Welcome
          fullscreen={fullscreen}
          showLogin={handleViewPage}
          processed={isProcessed}
        />
      ) : (
        <div
          className={`app-admin-wrap layout-sidebar-large ${darkMode === true ? "dark dj" : "light dj"
            }`}
        >
          <Notify position="TopRight" />
          <HeaderIndex
            darkMode={darkMode}
            designChange={designChange}
            isVisibleSide={isVisibleSide}
            isSidebar={isSidebar}
            setIsVisibleSide={setIsVisibleSide}
          />
          <div className="main-content-wrap home sidenav-open d-flex flex-column">
            <div className="marginTop">
              {!errorComponent.show &&
                <Container id="alerts-container">
                  <div className="verify-alt">
                    {combinedAlerts.length > 0 && (
                      <div>
                        {
                          !showAllAlerts && <Alert variant="light" className="mt-1 not_verify_suggest">
                            <Image
                              src={
                                "https://d9apl513jlx0u.cloudfront.net/public_assets/public/assets/images/icons/user_error.svg"
                              }
                              alt='error icon'
                            />{" "}
                            Hey{" "}
                            <span className="uppercaseText">
                              {profile.data.username}
                            </span>
                            ,
                            Your account has pending items!
                            <Button
                              variant="primary"
                              className="btn-sm pull-right credit"
                              type="button"
                              onClick={() => setShowAllAlerts(true)}
                            >
                              Review Now
                            </Button>
                          </Alert>
                        }
                        {
                          showAllAlerts && combinedAlerts.map((alert, index) => (
                            <div key={index}>{alert}</div>
                          ))
                        }
                        {
                          showAllAlerts &&
                          <div className="hide-all-btn-center">
                            <Button
                              variant="primary"
                              className="btn-sm pull-right credit"
                              type="button"
                              onClick={() => setShowAllAlerts(false)}
                            >
                              Hide Alerts
                            </Button>
                          </div>
                        }
                      </div>
                    )}
                  </div>
                </Container>
              }
              <div className="main-wrap-sec">
                {!errorComponent.show || path === "/logout" ?
                  <div className={ (!singlePost.error && !userDetails.error) ? "wrapper-container" : ""}>
                    {React.cloneElement(props.children, { darkMode })}
                  </div> :
                  <Error />
                }
              </div>
              <Footer darkMode={darkMode} />
            </div>
          </div>
          <UnverifiedPerformerModal
            show={unverifiedPerformerModal.show}
            handleClose={handleUnverifiedDialogClose}
          />

          <PerformerProfilePictureMissing
            show={performerMissingProfilePicture.show}
            handleClose={handlePerformerProfilePictureMissingDialogClose}
          />

          <CardAddressMismatchModal
            show={cardAddressMismatchModel.show}
            message={cardAddressMismatchModel.errorMessage}
            handleClose={handleCardAddressMismatchModalClose}
          />

          <CardManualReviewRequiredModal
            show={cardManualReviewRequiredModel.show}
            handleClose={handleCardManualReviewRequiredModalClose}
          />

          <F2FPackageSelection />
        </div>
      )}
    </>
  );
};

export default MainLayout;
